import { useState, useRef, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { ListItem, ListMemberRole, Recipe, RecipeIngredient, RecipeItemMetadata } from '@cleverlist/types';
import { Button, Input, Checkbox, CookingModeView } from '@cleverlist/ui';
import { useAuthStore } from '@cleverlist/store';
import { useApi } from '../providers/ApiProvider';
import { useListChannel } from '../hooks/useListChannel';
import { ShareModal } from '../components/ShareModal';

function formatTime(minutes: number | null | undefined): string {
  if (!minutes) return '';
  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  }
  return `${minutes}m`;
}

export function ListDetailPage() {
  const { listId } = useParams<{ listId: string }>();
  const [newItemName, setNewItemName] = useState('');
  const [shareOpen, setShareOpen] = useState(false);
  const [importUrl, setImportUrl] = useState('');
  const [selectedRecipe, setSelectedRecipe] = useState<Recipe | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [cookingRecipe, setCookingRecipe] = useState<Recipe | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const api = useApi();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  useListChannel(listId);

  const { data: list, isLoading: listLoading } = useQuery({
    queryKey: ['list', listId],
    queryFn: () => api.getList(Number(listId)),
    enabled: !!listId,
  });

  const { data: items, isLoading: itemsLoading } = useQuery({
    queryKey: ['listItems', listId],
    queryFn: () => api.getListItems(Number(listId)),
    enabled: !!listId,
  });

  const { data: members } = useQuery({
    queryKey: ['listMembers', listId],
    queryFn: () => api.getListMembers(Number(listId)),
    enabled: !!listId,
  });

  const currentMember = members?.find((m) => m.user_id === user?.id);
  const userRole: ListMemberRole = currentMember?.role ?? 'viewer';
  const isOwner = userRole === 'owner';
  const canEdit = userRole === 'owner' || userRole === 'editor';
  const otherMemberCount = (members?.length ?? 1) - 1;

  const addItemMutation = useMutation({
    mutationFn: (name: string) =>
      api.createListItem(Number(listId), { name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['listItems', listId] });
      setNewItemName('');
      inputRef.current?.focus();
    },
  });

  const updateItemMutation = useMutation({
    mutationFn: ({ id, completed }: { id: number; completed: boolean }) =>
      api.updateListItem(id, { completed }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['listItems', listId] });
    },
  });

  const deleteItemMutation = useMutation({
    mutationFn: (id: number) => api.deleteListItem(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['listItems', listId] });
    },
  });

  const importRecipeMutation = useMutation({
    mutationFn: (url: string) => api.importRecipe({ url, list_id: Number(listId) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['listItems', listId] });
      setImportUrl('');
    },
    onError: (error: any) => {
      alert(error?.response?.data?.message || error?.message || 'Failed to import recipe');
    },
  });

  const deleteRecipeMutation = useMutation({
    mutationFn: (recipeId: number) => api.deleteRecipe(recipeId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['listItems', listId] });
      setSelectedRecipe(null);
      setDeleteConfirm(null);
    },
  });

  // Keyboard shortcut: Cmd/Ctrl+N to focus new item input
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'n') {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleAddItem = (e: React.FormEvent) => {
    e.preventDefault();
    if (newItemName.trim()) {
      addItemMutation.mutate(newItemName.trim());
    }
  };

  const handleToggleItem = (item: ListItem) => {
    updateItemMutation.mutate({ id: item.id, completed: !item.completed });
  };

  const handleImportRecipe = (e: React.FormEvent) => {
    e.preventDefault();
    if (importUrl.trim()) {
      importRecipeMutation.mutate(importUrl.trim());
    }
  };

  const handleDeleteRecipe = (recipeId: number) => {
    if (deleteConfirm === recipeId) {
      deleteRecipeMutation.mutate(recipeId);
    } else {
      setDeleteConfirm(recipeId);
    }
  };

  if (listLoading || itemsLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!list) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">List not found</p>
        <Link to="/" className="text-blue-600 hover:underline mt-2 inline-block">
          Back to lists
        </Link>
      </div>
    );
  }

  const isRecipeList = list.type === 'recipe';
  const uncheckedItems = items?.filter((item: ListItem) => !item.completed) || [];
  const checkedItems = items?.filter((item: ListItem) => item.completed) || [];

  // Extract recipes from list items for recipe lists
  const recipes: Recipe[] = isRecipeList
    ? (items || [])
        .map((item: ListItem) => {
          const metadata = item.metadata as RecipeItemMetadata;
          return metadata?.recipe;
        })
        .filter((recipe): recipe is Recipe => !!recipe)
    : [];

  // Group ingredients by group_name for recipe detail view
  const ingredientsByGroup = selectedRecipe?.ingredients?.reduce<Record<string, RecipeIngredient[]>>(
    (acc, ingredient) => {
      const groupName = ingredient.group_name || '_default';
      if (!acc[groupName]) {
        acc[groupName] = [];
      }
      acc[groupName].push(ingredient);
      return acc;
    },
    {}
  ) || {};

  const hasNutrition =
    selectedRecipe?.nutrition &&
    (selectedRecipe.nutrition.calories ||
      selectedRecipe.nutrition.protein ||
      selectedRecipe.nutrition.carbs ||
      selectedRecipe.nutrition.fat);

  return (
    <div>
      <div className="mb-6">
        <Link to="/" className="text-sm text-gray-500 hover:text-gray-700 mb-2 inline-block">
          &larr; Back to lists
        </Link>
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-gray-900">{list.name}</h1>
          {otherMemberCount > 0 && (
            <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
              Shared with {otherMemberCount} {otherMemberCount === 1 ? 'person' : 'people'}
            </span>
          )}
          {isOwner && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShareOpen(true)}
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
              </svg>
              Share
            </Button>
          )}
        </div>
      </div>

      {/* Recipe List View */}
      {isRecipeList && (
        <>
          {/* URL Import Form */}
          {canEdit && (
            <form onSubmit={handleImportRecipe} className="mb-6">
              <div className="flex gap-2">
                <Input
                  ref={inputRef}
                  value={importUrl}
                  onChange={(e) => setImportUrl(e.target.value)}
                  placeholder="Paste recipe URL to import..."
                  className="flex-1"
                />
                <Button type="submit" loading={importRecipeMutation.isPending}>
                  Import
                </Button>
              </div>
              {importRecipeMutation.isError && (
                <p className="mt-2 text-sm text-red-600">
                  {(importRecipeMutation.error as { message?: string })?.message || 'Failed to import recipe'}
                </p>
              )}
            </form>
          )}

          {/* Empty state */}
          {recipes.length === 0 && (
            <div className="text-center py-12 bg-white rounded-lg shadow-sm">
              <div className="text-4xl mb-4">&#x1F373;</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No recipes yet</h3>
              <p className="text-gray-500">
                Import a recipe by pasting a URL above.
              </p>
            </div>
          )}

          {/* Recipe grid */}
          {recipes.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {recipes.map((recipe: Recipe) => (
                <div
                  key={recipe.id}
                  className="bg-white rounded-lg shadow-sm overflow-hidden group relative cursor-pointer"
                  onClick={() => setSelectedRecipe(recipe)}
                >
                  {/* Image */}
                  <div className="aspect-video bg-gray-100 flex items-center justify-center">
                    {recipe.thumbnail_url || recipe.image_url ? (
                      <img
                        src={recipe.thumbnail_url || recipe.image_url || ''}
                        alt={recipe.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-4xl">&#x1F37D;</span>
                    )}
                  </div>

                  {/* Content */}
                  <div className="p-4">
                    <h3 className="font-medium text-gray-900 truncate">{recipe.name}</h3>
                    {recipe.source_domain && (
                      <p className="text-sm text-gray-500 truncate">{recipe.source_domain}</p>
                    )}

                    {/* Meta row */}
                    <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                      {recipe.total_time && (
                        <span className="flex items-center gap-1">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                          {formatTime(recipe.total_time)}
                        </span>
                      )}
                      {recipe.servings && (
                        <span className="flex items-center gap-1">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                            />
                          </svg>
                          {recipe.servings}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Delete button */}
                  {canEdit && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteRecipe(recipe.id);
                      }}
                      className={`absolute top-2 right-2 p-2 rounded-full transition-all ${
                        deleteConfirm === recipe.id
                          ? 'bg-red-500 text-white opacity-100'
                          : 'bg-black/50 text-white opacity-0 group-hover:opacity-100'
                      }`}
                      onBlur={() => setDeleteConfirm(null)}
                      title={deleteConfirm === recipe.id ? 'Click again to confirm' : 'Delete recipe'}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Recipe Detail Modal */}
          {selectedRecipe && (
            <div
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
              onClick={() => setSelectedRecipe(null)}
            >
              <div
                className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Modal Header */}
                <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
                  <h2 className="text-xl font-bold text-gray-900">{selectedRecipe.name}</h2>
                  <div className="flex items-center gap-2">
                    {selectedRecipe.steps && selectedRecipe.steps.length > 0 && (
                      <button
                        onClick={() => setCookingRecipe(selectedRecipe)}
                        className="flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z" />
                        </svg>
                        Start Cooking
                      </button>
                    )}
                    {canEdit && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteRecipe(selectedRecipe.id)}
                        className={deleteConfirm === selectedRecipe.id ? 'text-red-600' : ''}
                      >
                        {deleteConfirm === selectedRecipe.id ? 'Confirm Delete' : 'Delete'}
                      </Button>
                    )}
                    <button
                      onClick={() => setSelectedRecipe(null)}
                      className="p-2 hover:bg-gray-100 rounded-full"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Modal Content */}
                <div className="p-6">
                  {/* Hero Image */}
                  {selectedRecipe.image_url && (
                    <div className="aspect-video w-full max-h-80 overflow-hidden rounded-lg mb-6">
                      <img
                        src={selectedRecipe.image_url}
                        alt={selectedRecipe.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}

                  {/* Source Link */}
                  {selectedRecipe.source_url && selectedRecipe.source_domain && (
                    <a
                      href={selectedRecipe.source_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:underline mb-3 inline-block"
                    >
                      View on {selectedRecipe.source_domain}
                    </a>
                  )}

                  {/* Description */}
                  {selectedRecipe.description && (
                    <p className="text-gray-600 mb-4">{selectedRecipe.description}</p>
                  )}

                  {/* Meta Row */}
                  <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-6">
                    {selectedRecipe.prep_time && (
                      <span className="flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        Prep: {formatTime(selectedRecipe.prep_time)}
                      </span>
                    )}
                    {selectedRecipe.cook_time && (
                      <span className="flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z"
                          />
                        </svg>
                        Cook: {formatTime(selectedRecipe.cook_time)}
                      </span>
                    )}
                    {selectedRecipe.total_time && (
                      <span className="flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        Total: {formatTime(selectedRecipe.total_time)}
                      </span>
                    )}
                    {selectedRecipe.servings && (
                      <span className="flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                          />
                        </svg>
                        {selectedRecipe.servings} servings
                      </span>
                    )}
                    {selectedRecipe.difficulty && (
                      <span className="flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13 10V3L4 14h7v7l9-11h-7z"
                          />
                        </svg>
                        {selectedRecipe.difficulty.charAt(0).toUpperCase() + selectedRecipe.difficulty.slice(1)}
                      </span>
                    )}
                  </div>

                  {/* Two-Column Layout */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Left Column - Ingredients */}
                    <div className="md:col-span-1">
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Ingredients</h3>

                        {Object.entries(ingredientsByGroup).map(([groupName, ingredients]) => (
                          <div key={groupName} className="mb-4 last:mb-0">
                            {groupName !== '_default' && (
                              <h4 className="text-sm font-medium text-gray-700 mb-2">{groupName}</h4>
                            )}
                            <ul className="space-y-2">
                              {ingredients.map((ingredient) => (
                                <li key={ingredient.id} className="text-sm text-gray-600">
                                  {ingredient.quantity && (
                                    <span className="font-medium">{ingredient.quantity}</span>
                                  )}{' '}
                                  {ingredient.unit && <span>{ingredient.unit}</span>}{' '}
                                  <span>{ingredient.name}</span>
                                  {ingredient.notes && (
                                    <span className="text-gray-400"> ({ingredient.notes})</span>
                                  )}
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}

                        {(!selectedRecipe.ingredients || selectedRecipe.ingredients.length === 0) && (
                          <p className="text-sm text-gray-500">No ingredients listed</p>
                        )}
                      </div>
                    </div>

                    {/* Right Column - Steps */}
                    <div className="md:col-span-2">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Instructions</h3>

                      {selectedRecipe.steps && selectedRecipe.steps.length > 0 ? (
                        <ol className="space-y-4">
                          {selectedRecipe.steps.map((step, index) => (
                            <li key={step.id} className="flex gap-4">
                              {/* Step Number Badge */}
                              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-semibold text-sm">
                                {index + 1}
                              </div>

                              <div className="flex-1">
                                <p className="text-gray-700">{step.instruction}</p>

                                {step.duration && (
                                  <p className="text-sm text-gray-500 mt-1">
                                    Duration: {formatTime(step.duration)}
                                  </p>
                                )}

                                {step.image_url && (
                                  <img
                                    src={step.image_url}
                                    alt={`Step ${index + 1}`}
                                    className="mt-3 rounded-lg max-w-sm"
                                  />
                                )}
                              </div>
                            </li>
                          ))}
                        </ol>
                      ) : (
                        <p className="text-sm text-gray-500">No instructions listed</p>
                      )}
                    </div>
                  </div>

                  {/* Nutrition Card */}
                  {hasNutrition && (
                    <div className="mt-6 bg-gray-50 rounded-lg p-4">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Nutrition Information</h3>

                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                        {selectedRecipe.nutrition?.calories && (
                          <div className="text-center p-3 bg-white rounded-lg">
                            <div className="text-2xl font-bold text-gray-900">
                              {selectedRecipe.nutrition.calories}
                            </div>
                            <div className="text-sm text-gray-500">Calories</div>
                          </div>
                        )}
                        {selectedRecipe.nutrition?.protein && (
                          <div className="text-center p-3 bg-white rounded-lg">
                            <div className="text-2xl font-bold text-gray-900">
                              {selectedRecipe.nutrition.protein}g
                            </div>
                            <div className="text-sm text-gray-500">Protein</div>
                          </div>
                        )}
                        {selectedRecipe.nutrition?.carbs && (
                          <div className="text-center p-3 bg-white rounded-lg">
                            <div className="text-2xl font-bold text-gray-900">
                              {selectedRecipe.nutrition.carbs}g
                            </div>
                            <div className="text-sm text-gray-500">Carbs</div>
                          </div>
                        )}
                        {selectedRecipe.nutrition?.fat && (
                          <div className="text-center p-3 bg-white rounded-lg">
                            <div className="text-2xl font-bold text-gray-900">
                              {selectedRecipe.nutrition.fat}g
                            </div>
                            <div className="text-sm text-gray-500">Fat</div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Regular List View (non-recipe) */}
      {!isRecipeList && (
        <>
          {/* Add new item -- editors and owners only */}
          {canEdit && (
            <form onSubmit={handleAddItem} className="mb-6">
              <div className="flex gap-2">
                <Input
                  ref={inputRef}
                  value={newItemName}
                  onChange={(e) => setNewItemName(e.target.value)}
                  placeholder="Add an item..."
                  className="flex-1"
                />
                <Button type="submit" loading={addItemMutation.isPending}>
                  Add
                </Button>
              </div>
              <p className="text-xs text-gray-400 mt-1">
                Tip: Press Cmd+N to quickly add items
              </p>
            </form>
          )}

          {/* Unchecked items */}
          <div className="bg-white rounded-lg shadow-sm divide-y">
            {uncheckedItems.length === 0 && checkedItems.length === 0 && (
              <div className="p-8 text-center text-gray-500">
                No items yet. Add your first item above!
              </div>
            )}
            {uncheckedItems.map((item: ListItem) => (
              <div
                key={item.id}
                className="flex items-center gap-3 p-3 hover:bg-gray-50 group"
              >
                <Checkbox
                  checked={item.completed}
                  onChange={() => handleToggleItem(item)}
                  disabled={!canEdit}
                />
                <span className="flex-1">{item.name}</span>
                {canEdit && (
                  <button
                    onClick={() => deleteItemMutation.mutate(item.id)}
                    className="text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Checked items */}
          {checkedItems.length > 0 && (
            <div className="mt-6">
              <h2 className="text-sm font-medium text-gray-500 mb-2">
                Completed ({checkedItems.length})
              </h2>
              <div className="bg-white rounded-lg shadow-sm divide-y opacity-60">
                {checkedItems.map((item: ListItem) => (
                  <div
                    key={item.id}
                    className="flex items-center gap-3 p-3 hover:bg-gray-50 group"
                  >
                    <Checkbox
                      checked={item.completed}
                      onChange={() => handleToggleItem(item)}
                      disabled={!canEdit}
                    />
                    <span className="flex-1 line-through text-gray-500">{item.name}</span>
                    {canEdit && (
                      <button
                        onClick={() => deleteItemMutation.mutate(item.id)}
                        className="text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      <ShareModal
        listId={Number(listId)}
        isOpen={shareOpen}
        onClose={() => setShareOpen(false)}
      />

      {cookingRecipe && cookingRecipe.steps && cookingRecipe.steps.length > 0 && (
        <CookingModeView
          recipe={{ name: cookingRecipe.name, steps: cookingRecipe.steps }}
          onExit={() => setCookingRecipe(null)}
        />
      )}
    </div>
  );
}
